import React from 'react';
import { Cell, flexRender, Row } from '@tanstack/react-table';
import { useTheme } from '@mui/material/styles';
import { Fade, Skeleton } from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import classNames from 'classnames';

import { CardRole, getCardLabel, getColumnMeta, resolveCardRole } from '../tableMeta';
import { useStyles } from './TableCardList.styles';
import { ThemeProps } from '@styles/theme';
import { messages } from '@shared/components/components.messages';

type Props<T> = {
    rows: Row<T>[],
    isLoading?: boolean,
    useSkeleton?: boolean,
    skeletonCount: number,
    scrollMode?: boolean,
    renderRowSubComponent?: Function,
    getRowClassName?: (...args: any) => string | undefined,
    getRowOnClick?: (...args: any) => void,
};

type RowData = {
    isDisabled?: boolean,
};

const isEmptyValue = (value: unknown): boolean =>
    value === null
    || value === undefined
    || value === ''
    || (Array.isArray(value) && value.length === 0);

const bucketCells = <T,>(row: Row<T>) => {
    const buckets: Record<CardRole, Cell<T, unknown>[]> = {
        title: [], subtitle: [], chip: [], badge: [], detail: [], actions: [], selection: [], hidden: [],
    };

    const firstAccessorColumnId = row
        .getAllCells()
        .find(cell => cell.column.accessorFn)?.column.id;

    row.getAllCells().forEach(cell => {
        const role = resolveCardRole(cell.column, cell.column.id === firstAccessorColumnId);
        if (getColumnMeta(cell.column).hideCardWhenEmpty && isEmptyValue(cell.getValue())) {
            return;
        }
        buckets[role].push(cell);
    });

    const byOrder = (a: Cell<T, unknown>, b: Cell<T, unknown>) =>
        ((a.column.columnDef.meta?.cardOrder ?? 0) - (b.column.columnDef.meta?.cardOrder ?? 0));
    (Object.keys(buckets) as CardRole[]).forEach(role => buckets[role].sort(byOrder));

    return buckets;
};

const renderCell = <T,>(cell: Cell<T, unknown>) =>
    flexRender(cell.column.columnDef.cell, cell.getContext());

export function TableCardList<T extends object = {}>({
    rows,
    isLoading,
    useSkeleton,
    skeletonCount,
    scrollMode,
    renderRowSubComponent,
    getRowClassName,
    getRowOnClick,
}: Props<T>) {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const listClassName = classNames(styles.cardList, {
        [styles.cardListFlow]: !scrollMode,
    });

    if (isLoading && useSkeleton) {
        return (
            <div className={listClassName}>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <Fade key={`card_skeleton_${index}`} in timeout={1000}>
                        <div className={styles.card}>
                            <Skeleton className={styles.skeletonLine} variant="rectangular" width="60%" height={20} />
                            <Skeleton variant="rectangular" width="90%" height={14} />
                        </div>
                    </Fade>
                ))}
            </div>
        );
    }

    return (
        <div className={listClassName}>
            {rows.map(row => {
                const buckets = bucketCells(row);
                const rowData = row.original as RowData;
                const isExpandable = renderRowSubComponent !== undefined && row.getCanExpand();

                return (
                    <Fade key={row.id} in timeout={1000}>
                        <div
                            className={classNames(
                                styles.card,
                                getRowClassName?.(row.original, row),
                                {
                                    disabled: Boolean(rowData?.isDisabled),
                                    [styles.cardClickable]: Boolean(getRowOnClick),
                                }
                            )}
                            onClick={() => getRowOnClick?.(row.original, row)}
                        >
                            <div className={styles.cardHeader}>
                                {buckets.selection.map(cell => (
                                    <span
                                        key={cell.id}
                                        className={styles.cardSelection}
                                        onClick={event => event.stopPropagation()}
                                    >
                                        {renderCell(cell)}
                                    </span>
                                ))}

                                <div className={styles.cardTitleBlock}>
                                    {buckets.title.map(cell => (
                                        <div key={cell.id} className={styles.cardTitle}>
                                            {renderCell(cell)}
                                        </div>
                                    ))}
                                    {buckets.subtitle.map(cell => (
                                        <div key={cell.id} className={styles.cardSubtitle}>
                                            {renderCell(cell)}
                                        </div>
                                    ))}
                                </div>

                                {buckets.badge.length > 0 && (
                                    <div className={styles.cardBadge}>
                                        {buckets.badge.map(cell => (
                                            <React.Fragment key={cell.id}>
                                                {renderCell(cell)}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}

                                {isExpandable && (
                                    <button
                                        type="button"
                                        className={styles.expandButton}
                                        aria-label={messages['tableCard.toggleDetails']}
                                        title={messages['tableCard.toggleDetails']}
                                        onClick={event => {
                                            event.stopPropagation();
                                            row.toggleExpanded();
                                        }}
                                    >
                                        <ExpandMoreOutlinedIcon
                                            className={classNames(styles.expandIcon, {
                                                [styles.expandIconOpen]: row.getIsExpanded(),
                                            })}
                                        />
                                    </button>
                                )}
                            </div>

                            {buckets.chip.length > 0 && (
                                <div className={styles.cardChips}>
                                    {buckets.chip.map(cell => (
                                        <React.Fragment key={cell.id}>
                                            {renderCell(cell)}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}

                            {buckets.detail.length > 0 && (
                                <dl className={styles.cardDetails}>
                                    {buckets.detail.map(cell => (
                                        <React.Fragment key={cell.id}>
                                            <dt className={styles.cardDetailLabel}>
                                                {getCardLabel(cell.column)}
                                            </dt>
                                            <dd className={styles.cardDetailValue}>
                                                {renderCell(cell)}
                                            </dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            )}

                            {isExpandable && row.getIsExpanded() && (
                                <div className={styles.cardExpanded}>
                                    {renderRowSubComponent?.({ row })}
                                </div>
                            )}

                            {buckets.actions.length > 0 && (
                                <div
                                    className={styles.cardActions}
                                    onClick={event => event.stopPropagation()}
                                >
                                    {buckets.actions.map(cell => (
                                        <React.Fragment key={cell.id}>
                                            {renderCell(cell)}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Fade>
                );
            })}
        </div>
    );
}
